import { motion } from "framer-motion";
import { CountUp, growX, Wipe } from "./lib";
import { planet } from "./content";
import { ChartCard, SimpleTable } from "./parts";

const fmt = (n: number) => n.toLocaleString("en-US");
const VIEW = { once: true, amount: 0.15 } as const;
const EASE = [0.22, 1, 0.36, 1] as const;

export function GhgChart() {
  const W = 560, H = 300, L = 48, R = 40, T = 24, B = 40;
  const max = 140000, imax = 9;
  const n = planet.ghg.length, slot = (W - L - R) / n, bw = Math.min(54, slot * 0.55);
  const y = (v: number) => T + (H - T - B) * (1 - v / max);
  const yi = (v: number) => T + (H - T - B) * (1 - v / imax);
  const cx = (i: number) => L + slot * i + slot / 2;
  const target = Math.round(planet.ghg[0].s1 + planet.ghg[0].s2) / 2;
  const line = planet.ghg.map((d, i) => `${i ? "L" : "M"}${cx(i)},${yi(d.intensity)}`).join(" ");
  return (
    <ChartCard title="Scope 1 and 2 GHG emissions and intensity by year" subtitle="MT CO2e (bars) and MT CO2e per $M revenue (line)" page={64} testId="am-chart-ghg"
      table={<SimpleTable caption="Scope 1 and 2 emissions" head={["Year", "Scope 1", "Scope 2 (market)", "Total", "Intensity"]} rows={planet.ghg.map((d) => [d.year, fmt(d.s1), fmt(d.s2), fmt(d.s1 + d.s2), d.intensity.toFixed(1)])} />}>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label="Stacked bars of Scope 1 and Scope 2 emissions from FY 2019 to FY 2025 with intensity falling from 7.8 to 4.0">
        {[0, 35000, 70000, 105000, 140000].map((v) => (
          <g key={v}>
            <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="#dfe3e8" />
            <text x={L - 8} y={y(v) + 4} textAnchor="end" fontSize="11" fill="#53565a">{`${v / 1000}k`}</text>
          </g>
        ))}
        {planet.ghg.map((d, i) => (
          <motion.g key={d.year} style={{ originY: 1 }} initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} viewport={VIEW} transition={{ duration: 1, delay: i * 0.08, ease: EASE }}>
            <rect x={cx(i) - bw / 2} y={y(d.s2)} width={bw} height={y(0) - y(d.s2)} fill="#4f840e" />
            <rect x={cx(i) - bw / 2} y={y(d.s1 + d.s2)} width={bw} height={y(0) - y(d.s1)} fill="#f8c642" />
          </motion.g>
        ))}
        <line x1={L} x2={W - R} y1={y(target)} y2={y(target)} stroke="#7fc3df" strokeWidth="2" />
        <motion.path d={line} fill="none" stroke="#6245ba" strokeWidth="2" strokeDasharray="5 4" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={VIEW} transition={{ duration: 1.4, delay: 0.5, ease: EASE }} />
        {planet.ghg.map((d, i) => (
          <g key={`i${d.year}`}>
            <circle cx={cx(i)} cy={yi(d.intensity)} r="4" fill="#6245ba" />
            <text x={cx(i)} y={yi(d.intensity) - 9} textAnchor="middle" fontSize="12" fontWeight="700" fill="#6245ba">{d.intensity.toFixed(1)}</text>
            <text x={cx(i)} y={H - B + 18} textAnchor="middle" fontSize="11" fill="#53565a">{d.year}</text>
            {d.label && <text x={cx(i)} y={H - B + 32} textAnchor="middle" fontSize="10" fill="#53565a">{d.label}</text>}
          </g>
        ))}
      </svg>
      <div className="mt-3 flex flex-wrap gap-4 text-xs">
        <span className="flex items-center gap-2"><i className="h-3 w-3 bg-[#4f840e]" />Scope 2 (market-based)</span>
        <span className="flex items-center gap-2"><i className="h-3 w-3 bg-[#f8c642]" />Scope 1 (direct emissions)</span>
        <span className="flex items-center gap-2"><i className="h-0.5 w-4 bg-[#7fc3df]" />2030 target, 50% below FY 2019</span>
        <span className="flex items-center gap-2"><i className="h-0.5 w-4 border-t-2 border-dashed border-[#6245ba]" />GHG intensity per $M revenue</span>
      </div>
    </ChartCard>
  );
}

export function RenewableChart() {
  return (
    <ChartCard title="Electricity matched with renewable sources" subtitle="% of electricity, global and U.S." page={66} testId="am-chart-renewable"
      table={<SimpleTable caption="Renewable electricity rate" head={["Year", "Global", "U.S."]} rows={planet.renewable.map((r) => [r.year, `${r.global}%`, `${r.us}%`])} />}>
      <ul className="space-y-4">
        {planet.renewable.map((r, i) => (
          <li key={r.year} className="grid grid-cols-[64px_1fr] items-center gap-3 text-sm">
            <span>{r.year}</span>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="h-3 flex-1 bg-[var(--am-mist)]"><motion.div className="h-full origin-left bg-[var(--am-green)]" style={{ width: `${r.global}%` }} {...growX(i * 0.07)} /></div>
                <span className="w-11 text-right font-semibold am-accent">{r.global}%</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-1.5 flex-1 bg-[var(--am-mist)]"><motion.div className="h-full origin-left bg-[var(--am-blue)]" style={{ width: `${r.us}%` }} {...growX(0.1 + i * 0.07)} /></div>
                <span className="w-11 text-right text-xs">{r.us}%</span>
              </div>
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex gap-4 text-xs">
        <span className="flex items-center gap-2"><i className="h-3 w-3 bg-[var(--am-green)]" />Global</span>
        <span className="flex items-center gap-2"><i className="h-1.5 w-3 bg-[var(--am-blue)]" />U.S.</span>
      </div>
    </ChartCard>
  );
}

const S3_COLORS = ["#4f840e", "#347da2", "#6245ba", "#f8c642", "#7fc3df", "#b9c0c8"];

export function Scope3Chart() {
  const total = planet.scope3Total;
  return (
    <ChartCard title="Where Scope 3 emissions come from" subtitle={`FY 2025 total: ${fmt(total)} MT CO2e`} page={65} testId="am-chart-scope3"
      table={<SimpleTable caption="Scope 3 by category FY 2025" head={["Category", "MT CO2e", "Share"]} rows={planet.scope3.map((s) => [s.label, fmt(s.value), `${((s.value / total) * 100).toFixed(1)}%`])} />}>
      <CountUp value={`${((planet.scope3[0].value / total) * 100).toFixed(0)}%`} className="block text-5xl font-semibold am-accent" />
      <p className="mt-1 text-sm">of Scope 3 comes from the use of sold products (Category 11)</p>
      <Wipe>
        <div className="mt-5 flex h-8 w-full overflow-hidden" aria-hidden>
          {planet.scope3.map((s, i) => <div key={s.label} style={{ width: `${(s.value / total) * 100}%`, background: S3_COLORS[i] }} />)}
        </div>
      </Wipe>
      <ul className="mt-4 space-y-1.5 text-xs">
        {planet.scope3.map((s, i) => (
          <li key={s.label} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2"><i className="h-2.5 w-2.5 shrink-0" style={{ background: S3_COLORS[i] }} />{s.label}</span>
            <span className="tabular-nums">{((s.value / total) * 100).toFixed(1)}%</span>
          </li>
        ))}
      </ul>
      <p className="mt-6 border-t border-[var(--am-line)] pt-4 text-sm">Scope 3 represents more than 99% of Applied’s carbon footprint.</p>
    </ChartCard>
  );
}

export function WaterChart() {
  const base = planet.water[0].intensity, now = planet.water[planet.water.length - 1].intensity;
  return (
    <ChartCard title="Water withdrawal intensity" subtitle="Thousand m3 per $M revenue" page={67} testId="am-chart-water"
      table={<SimpleTable caption="Water withdrawal" head={["Year", "Withdrawal (thousand m3)", "Intensity per $M revenue"]} rows={planet.water.map((w) => [w.year, fmt(w.withdrawal), w.intensity.toFixed(2)])} />}>
      <div className="flex items-end gap-6">
        <div>
          <CountUp value={now.toFixed(2)} className="block text-5xl font-semibold am-accent" />
          <p className="mt-1 text-sm">in FY 2025, down from {base.toFixed(2)} in FY 2019</p>
        </div>
      </div>
      <div className="mt-6 flex h-36 items-end gap-3" aria-hidden>
        {planet.water.map((w, i) => (
          <div key={w.year} className="flex flex-1 flex-col items-center">
            <span className="mb-1 text-xs font-semibold">{w.intensity.toFixed(2)}</span>
            <motion.div className="w-full max-w-[44px] origin-bottom bg-[var(--am-blue)]" style={{ height: `${Math.round((w.intensity / 0.16) * 110)}px`, opacity: i === 0 ? 0.45 : 1 }}
              initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} viewport={VIEW} transition={{ duration: 1, delay: i * 0.08, ease: EASE }} />
            <span className="mt-2 text-[11px]">{w.year}</span>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs">Total water withdrawal was {fmt(planet.water[planet.water.length - 1].withdrawal)} thousand m3 in FY 2025.</p>
    </ChartCard>
  );
}
